import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { jwtDecode } from "jwt-decode";
import {
  PostCardDetailsProps,
  TokenPayload,
  LeaderboardUpdatedProps,
  LeaderboardTab,
  LeaderBoardTabs,
} from "../../types/ComponentProps";
import Block from "../../components/blocks/Block";
import Allowance from "../../components/summary/Allowance";
import GiveablePoints from "../../components/summary/GiveablePoints";
import RedeemablePoints from "../../components/summary/RedeemablePoints";
import Coins from "../../components/summary/Coins";
import WithNavTemplate from "../../components/template/WithNavTemplate";
import SpecialRecognition from "../../components/specialRecognition/SpecialRecognition";
import LevelBlock from "../../components/level/LevelBlock";
import LeaderboardList from "../../components/leaderboard/LeaderboardList";
import PostCard from "../../components/postCard/PostCard";
import useGetUserPoint from "../../hooks/useGetUserPoint";
import useGetLeaderBoard from "../../hooks/useGetLeaderBoard";
import useGetSevenDaysLeaderboard from "../../hooks/useGetSevenDaysLeaderboard";
import useGetAllPosts from "../../hooks/useGetAllPosts";
import Spinner from "../../components/spinner/Spinner";
import HashTagBlock from "../../components/hashTagBlock/HashTagBlock";
import BadgeSelectionModal from "../../components/badgeSelectionModal/BadgeSelectionModal";
import AdminPostCard from "../../components/postCard/AdminPostCard";
import Navigation from "../../components/navigation/Navigation";
import LikesModal from "../../components/likesModal/LikesModal";
import EditPostModal from "../../components/editPostModal/EditPostModal";
import { useNotification } from "../../context/NotificationContext";
import LeaderboardDaysList from "../../components/leaderboard/LeaderboardDaysList";
import LeaderboardThirtyDaysList from "../../components/leaderboard/LeaderboardThirtyDaysList";
import useGetThirtyDaysLeaderboard from "../../hooks/useGetThirtyDaysLeaderboard";
import useGetNinetyDaysLeaderboard from "../../hooks/useGetNinetyDaysLeaderboard";
import LeaderboardNinetyDaysList from "../../components/leaderboard/LeaderboardNinetyDaysList";
import PostSectionContainer from "../../components/postSection/PostSectionContainer";
import { usePostForm } from "../../context/PostFormContext";
import { useModal } from "../../context/ModalContext";
import Modal from "../../components/modal/Modal";
import { useCommentLikeModal } from "../../context/CommentLikeModalContext";
import ReactionModal from "../../components/reactionModal/ReactionModal";
import ImagePostModal from "../../components/imagePostModal/ImagePostModal";
import { useImagePostModal } from "../../context/ImagePostModalContext";
import CommentDeleteModal from "../../components/commentDeleteModal/CommentDeleteModal";
import { useCommentDeleteModal } from "../../context/CommentDeleteModalContext";

const DashboardPage = () => {
  const {
    socketState,
    setNotificationModal,
    setSelectedPostNotification,
    setSelectedNotificationId,
  } = useNotification();

  const { postLoading: useFormLoading } = usePostForm();
  const {
    modalOpen,
    modalTitle,
    closeModal,
    confirmHandler,
    isLoading: modalLoading,
  } = useModal();

  const { isOpen: commentLikeModalIsOpen } = useCommentLikeModal();
  const { isOpen: imagePostModalIsOpen } = useImagePostModal();
  const { isOpen: commentDeleteModalIsOpen } = useCommentDeleteModal();

  const userDetails: TokenPayload = jwtDecode(
    JSON.stringify(localStorage.getItem("accessTokenGC")),
  );
  const { data } = useGetUserPoint();
  const { dataSevenDaysLeaderboard, sevenDaysLeaderboardIsLoading } =
    useGetSevenDaysLeaderboard();
  const { dataThirtyDaysLeaderboard, thirtyDaysLeaderboardIsLoading } =
    useGetThirtyDaysLeaderboard();
  const { dataNinetyDaysLeaderboard, NinetyLeaderboardIsLoading } =
    useGetNinetyDaysLeaderboard();
  const { leaderBoardList, isLoadingLeaderBoardList } = useGetLeaderBoard();

  const { ref, inView } = useInView();
  const {
    isLoading,
    data: allPosts,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useGetAllPosts();

  // States

  const [modalRecognition, setModalRecognition] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [selectedCardModal, setSelectedCardModal] = useState<boolean>(false);

  const [editPostModal, setEditPostModal] = useState<boolean>(false);
  const [editCardSelection, setEditCardSelection] = useState<number | null>(
    null,
  );

  const [isOpenLevel, setIsOpenLevel] = useState<boolean>(false);
  const leaderboardTabs: {
    key: keyof typeof LeaderBoardTabs;
    label: string;
  }[] = [
    { key: "ALL", label: "All" },
    { key: "SEVEN_DAYS", label: "7 Days" },
    { key: "THIRTY_DAYS", label: "30 Days" },
    { key: "NINETY_DAYS", label: "90 Days" },
  ];

  const [activeLeaderboardTab, setActiveLeaderboardTab] =
    useState<LeaderboardTab>(LeaderBoardTabs.THIRTY_DAYS);

  // UseEffect
  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage]);

  useEffect(() => {
    if (userDetails) {
      window.history.pushState(null, document.title, window.location.href);
      const handlePopState = () => {
        window.history.pushState(null, document.title, window.location.href);
      };
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    }
  }, [userDetails]);

  const onLeaderboardClickHandler = (tab: any) => {
    setActiveLeaderboardTab(tab);
  };

  const leaderboardUsers =
    leaderBoardList?.pages.flatMap(
      (user: LeaderboardUpdatedProps) => user.user,
    ) || [];

  if (!socketState) {
    return (
      <>
        <Navigation />
        <div className="inside-page-container-blk">
          <div
            className="main-container-blk"
            style={{
              position: "relative",
              height: "100vh",
              paddingTop: "200px",
            }}
          >
            <Spinner alignSpin="center-spinner" variant="large-spinner" />
          </div>
        </div>
      </>
    );
  }

  /* Modal */

  return (
    <WithNavTemplate
      socket={!socketState ? null : socketState}
      setNotificationModal={setNotificationModal}
      setSelectedPostNotification={setSelectedPostNotification}
      setSelectedNotificationId={setSelectedNotificationId}
    >
      {modalRecognition ? (
        <BadgeSelectionModal
          setModalRecognition={setModalRecognition}
          socket={socketState}
        />
      ) : null}
      {selectedCardModal ? (
        <LikesModal
          post={selectedCard}
          setSelectedCardModal={setSelectedCardModal}
        />
      ) : null}
      {commentLikeModalIsOpen ? <ReactionModal /> : null}
      {imagePostModalIsOpen ? <ImagePostModal /> : null}
      {commentDeleteModalIsOpen ? <CommentDeleteModal /> : null}
      {editPostModal ? (
        <EditPostModal
          setEditPostModal={setEditPostModal}
          editCardSelection={editCardSelection}
          setEditCardSelection={setEditCardSelection}
        />
      ) : null}
      <Modal
        title={modalTitle}
        modalOpen={modalOpen}
        setDeleteModal={closeModal}
        onSubmitHandler={confirmHandler}
        isLoading={modalLoading}
      />
      <div className="inside-page-container-blk">
        <div className="main-container-blk user-dashboard-main-section">
          <div
            className="left-section-dashboard"
            style={isOpenLevel ? { top: "-650px" } : { top: "-350px" }}
          >
            <Block title="Summary" navigation="dots">
              <Allowance />
              <GiveablePoints data={data} />
              <RedeemablePoints data={data} />
              <Coins data={data} />
            </Block>
            {userDetails?.isAdmin ? (
              <SpecialRecognition setModalRecognition={setModalRecognition} />
            ) : (
              <LevelBlock
                data={data}
                isOpenLevel={isOpenLevel}
                setIsOpenLevel={setIsOpenLevel}
              />
            )}

            <Block
              title="Leaderboard"
              navigation="viewall"
              linkURL="leaderboard"
            >
              <div className="leaderboard-tab-dashboard-blk">
                {leaderboardTabs.map(({ key, label }) => {
                  const value = LeaderBoardTabs[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      className={activeLeaderboardTab === value ? "active" : ""}
                      onClick={() => onLeaderboardClickHandler(value)}
                      aria-pressed={activeLeaderboardTab === value}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {activeLeaderboardTab === LeaderBoardTabs.ALL && (
                <LeaderboardList
                  leaderBoardListLoading={isLoadingLeaderBoardList}
                  leaderBoardList={leaderboardUsers}
                />
              )}
              {activeLeaderboardTab === LeaderBoardTabs.SEVEN_DAYS && (
                <LeaderboardDaysList
                  leaderboardData={dataSevenDaysLeaderboard}
                  loadingLeaderboard={sevenDaysLeaderboardIsLoading}
                />
              )}
              {activeLeaderboardTab === LeaderBoardTabs.THIRTY_DAYS && (
                <LeaderboardThirtyDaysList
                  leaderboardData={dataThirtyDaysLeaderboard}
                  loadingLeaderboard={thirtyDaysLeaderboardIsLoading}
                />
              )}
              {activeLeaderboardTab === LeaderBoardTabs.NINETY_DAYS && (
                <LeaderboardNinetyDaysList
                  leaderboardData={dataNinetyDaysLeaderboard}
                  loadingLeaderboard={NinetyLeaderboardIsLoading}
                />
              )}
            </Block>
          </div>
          <div className="center-section-dashboard">
            <PostSectionContainer />

            <hr />
            {isLoading || useFormLoading ? (
              <Spinner alignSpin="center-spinner" variant="medium-spinner" />
            ) : allPosts?.pages[0]?.posts.length === 0 ? (
              <p className="not-available-blk">No posts available</p>
            ) : (
              allPosts?.pages.map((page, index) => (
                <React.Fragment key={index}>
                  {page.posts.map(
                    (post: PostCardDetailsProps, index: number) =>
                      post?.badgeId ? (
                        <AdminPostCard
                          key={index}
                          post={post}
                          socket={socketState}
                          setSelectedCardModal={setSelectedCardModal}
                          setSelectedCard={setSelectedCard}
                        />
                      ) : (
                        <PostCard
                          key={index}
                          post={post}
                          socket={socketState}
                          setSelectedCardModal={setSelectedCardModal}
                          setSelectedCard={setSelectedCard}
                          setEditPostModal={setEditPostModal}
                          setEditCardSelection={setEditCardSelection}
                        />
                      ),
                  )}
                </React.Fragment>
              ))
            )}
            <div ref={ref}>
              {isLoading && isFetchingNextPage ? (
                <Spinner alignSpin="center-spinner" variant="medium-spinner" />
              ) : hasNextPage ? null : (
                <div className="all-posts-label-blk">
                  <div></div>
                  <p>You have seen all the posts</p>
                  <div></div>
                </div>
              )}
            </div>
          </div>

          <div className="right-section-dashboard">
            <Block title="Trending">
              <HashTagBlock />
            </Block>
          </div>
        </div>
      </div>
    </WithNavTemplate>
  );
};

export default DashboardPage;
